import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useSubMenu } from '../SubMenuContext';
import { AnimatePresence, motion } from 'framer-motion';
import { DropdownMenu } from './DropDown/DropdownMenu';
import { DropdownMenuTrigger } from './DropDown/DropdownMenuTrigger';
import { DropdownMenuContent } from './DropDown/DropdownMenuContent';
import { useState, ReactNode, useEffect, useRef, isValidElement, cloneElement, Children } from 'react';
import SkelletonLoader from './Skelleton';
import MinhaContaBtn from './SideBarBtns/MinhaContaBtn';
import AssinaturaBtn from './SideBarBtns/AssinaturaBtn';
import ItemIcon from './ItemIcon';
import ItemText from './ItemText';
import SairBtn from './SideBarBtns/SairBtn';
import { useCopyToClipboard } from '../lib/hook/copyClipBoard';
import { toast } from 'sonner';
import { ArrowLeftRight, CircleDollarSign, Lightbulb, MessageCircleQuestion } from 'lucide-react';
import { useTranslation } from '../lib/i18n/TranslationContext';
import ItemMenu from './ItemMenu';
import { EmpreenderApps } from '../lib/AppsArray';
import Profile from './Perfil/Profile';
import IlhaPerfil from './IlhaPerfil';
import ChaveButton from './ChaveButton';
import { useUser } from '../UserContext';
import Modal from './Modal/Modal';
import ModalTrigger from './Modal/ModalTrigger';
import ModalContent from './Modal/ModalContent';
import AppModal from './AppModal';
import { useChat } from '../context/ChatContext';

interface PerfilMenuProps {
	children?: ReactNode;
	name?: string;
	imagem_perfil?: string;
	chave_empreender?: string;
}

const PerfilMenu = ({ children }: PerfilMenuProps) => {
	const { t } = useTranslation();
	const { user, hideApps } = useUser();
	const { imagem_perfil, chave_empreender, loading, name } = user || {};

	const { isSidebarOpen, setChaveWidth, app } = useSubMenu();
	const [isOpen, setIsOpen] = useState(false);
	const chaveRef = useRef<HTMLSpanElement>(null);
	const { toggleChat } = useChat();

	const [, copy] = useCopyToClipboard();
	const matchApp = EmpreenderApps.find((a) => a.tags.some((tag) => tag?.toLowerCase() === app?.toLowerCase()));

	const handleOnOpen = (state: boolean) => {
		setIsOpen(state);
	};

	const MinhaContaBtnChildren = Children.map(children, (child) => {
		if (isValidElement(child) && child.type === MinhaContaBtn) {
			return cloneElement(child as React.ReactElement);
		}
		return null;
	});

	const AssinaturaBtnChildren = Children.map(children, (child) => {
		if (isValidElement(child) && child.type === AssinaturaBtn) {
			return cloneElement(child as React.ReactElement);
		}
		return null;
	});

	const SairBtnChildren = Children.map(children, (child) => {
		if (isValidElement(child) && child.type === SairBtn) {
			return cloneElement(child as React.ReactElement);
		}
		return null;
	});

	const copiarChave = () => {
		if (!chave_empreender) {
			toast.error('Não foi possível copiar a chave');
		} else {
			copy(chave_empreender);
		}
	};

	useEffect(() => {
		if (!chaveRef.current) return;

		const { width } = chaveRef.current.getBoundingClientRect();

		setChaveWidth?.(width + 60);
	}, [isOpen, chave_empreender]);

	return (
		<DropdownMenu isOpen={isOpen} setIsOpen={handleOnOpen}>
			{loading ? (
				<DropdownMenuTrigger disabled>
					<div className={`group w-full text-left bg-white !p-1.5 ${isSidebarOpen ? 'group-hover/container:bg-gray-100 cursor-pointer' : ''} transition-all h-[68px] rounded-lg flex items-center flex-row overflow-hidden gap-x-3 group`}>
						<div>
							<SkelletonLoader width={`${isSidebarOpen ? '36px' : '24px'}`} className={`${isSidebarOpen ? 'h-9' : 'h-6'} transition-all`} />
						</div>

						<AnimatePresence mode='popLayout'>
							{isSidebarOpen ? (
								<>
									<motion.div initial={{ opacity: 0, x: 0 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.1 }} exit={{ opacity: 0, x: 0 }} className='w-full flex flex-row items-center justify-between text-xs text-gray-900 gap-0.5'>
										<div className='flex flex-col gap-0.5'>
											<SkelletonLoader width='150px' />
											<div className='flex flex-row items-center gap-0.5 '>
												<span className='size-3.5'>
													<SkelletonLoader width='14px' />
												</span>
												<span>
													<SkelletonLoader width='100px' />
												</span>
												<span className='transition-all ease-in-out size-3.5 invisible opacity-0 group-hover:opacity-100 group-hover:visible'>
													<SkelletonLoader width='14px' className='transition-all' />
												</span>
											</div>
										</div>
									</motion.div>
								</>
							) : null}
						</AnimatePresence>
					</div>
				</DropdownMenuTrigger>
			) : (
				<DropdownMenuTrigger>
					<motion.div initial={{ padding: isSidebarOpen ? 12 : 0, outlineWidth: 0 }} animate={{ padding: isSidebarOpen ? 12 : 0, outlineWidth: 0 }} transition={{ duration: .2 }} className={`group w-full text-left bg-white ${isSidebarOpen ? 'group-hover/container:bg-gray-100 cursor-pointer !border-gray-200' : '!border-transparent'} !border h-[68px] rounded-lg flex items-center flex-row gap-x-3 group !outline-none`}>
						<div className='size-[36px] rounded-lg flex-shrink-0 overflow-hidden outline-none'>
							{imagem_perfil ? <img className='object-cover w-full h-full' src={imagem_perfil} /> : <div className='w-full h-full bg-gray-900 text-white flex items-center justify-center text-sm'>{name?.[0]}</div>}
						</div>
						<AnimatePresence mode='popLayout'>
							{isSidebarOpen ? (
								<>
									<motion.div initial={{ opacity: 0, x: 0 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.1 }} exit={{ opacity: 0, x: 0 }} className='w-full flex flex-row items-center justify-between text-xs text-gray-900 gap-0.5'>
										<div>
											<div className='font-bold text-sm break-normal whitespace-nowrap truncate w-[140px]'>{name}</div>
											<div className='flex flex-row items-center gap-0.5 '>
												<span className='break-normal whitespace-nowrap text-xs leading-4 font-normal tracking-normal text-gray-500 font-sans' ref={chaveRef}>
													{chave_empreender}
												</span>
											</div>
										</div>

										<motion.div className='size-3 ' animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25, delay: 0.1, ease: [0, 0.71, 0.2, 1.01] }}>
											<ChevronDownIcon className='stroke-[2.5px]' />
										</motion.div>
									</motion.div>
								</>
							) : null}
						</AnimatePresence>
					</motion.div>
				</DropdownMenuTrigger>
			)}
			<DropdownMenuContent side='bottom' alignOffset={2} sideOffset={6} align='start' className='border [&_.item-child-lb-empreender]:!gap-2 [&_.item-child-lb-empreender]:!p-0 w-[300px] rounded-xl border-gray-200 [&_svg]:stroke-[2.3] [&_svg]:size-5 text-gray-900 !p-0 shadow-xl !gap-6'>
				<Profile />
				<div className='flex gap-6 flex-col'>
					<div className='!p-4 flex flex-col gap-3 !pb-0 !pt-0'>
						<IlhaPerfil>
							{
								matchApp && matchApp.eplus != "PL" && <>

									{
										!hideApps ? <>
											<Modal>
												<ModalTrigger>
													<ItemMenu preventDefault className='!gap-2 !px-2 py-2.5'>
														<ItemIcon>
															<ArrowLeftRight />
														</ItemIcon>
														<ItemText skipSidebar>{t('Trocar de conta')}</ItemText>
													</ItemMenu>
												</ModalTrigger>
												<ModalContent className='h-auto'>
													<AppModal app={matchApp} />
												</ModalContent>
											</Modal>
											{
												AssinaturaBtnChildren?.length ? <div className='h-px bg-gray-200 w-full my-2' /> : null
											}
										</> : <></>
									}

								</>
							}

							{MinhaContaBtnChildren}
							{
								MinhaContaBtnChildren?.length ? <div className='h-px bg-gray-200 w-full my-2' /> : null
							}

							{AssinaturaBtnChildren}
						</IlhaPerfil>

						<IlhaPerfil>
							<ChaveButton chave_empreender={chave_empreender as string} copiarChave={copiarChave} />

							<div className='h-px bg-gray-200 w-full my-2' />
							{
								hideApps ? <></> : (
									<>
										<ItemMenu preventDefault asChild className='!gap-2 !px-2 py-2.5'>
											<a href='http://embaixadores.com.br/' target='_blank'>
												<ItemIcon>
													<CircleDollarSign />
												</ItemIcon>
												<ItemText skipSidebar>{t('Indique e Ganhe')}</ItemText>
											</a>
										</ItemMenu>
										<div className='h-px bg-gray-200 w-full my-2' />
									</>
								)
							}

							<ItemMenu preventDefault asChild className='!gap-2 !px-2 py-2.5'>
								<a href='https://forms.gle/Y7vNCoDSyfnzJjGr7' target='_blank'>
									<ItemIcon>
										<Lightbulb />
									</ItemIcon>
									<ItemText skipSidebar>{t('Feedback')}</ItemText>
								</a>
							</ItemMenu>
						</IlhaPerfil>

					</div>
					<div className='flex flex-col !p-4 !pt-0'>
						<div className='h-px w-full bg-gray-200 flex-shrink-0 !mb-3'></div>

						<div className='flex flex-nowrap flex-row w-full h-full items-center justify-center gap-2'>
							<ItemMenu preventDefault className='!gap-2 !px-2 py-2.5 !w-[123px] flex items-center justify-center' onClick={() => toggleChat(true)}>
								{/* <a href={matchApp?.suportLink || 'https://suporte.com.br'} target='_blank' className=''> */}
								<div className='flex flex-row gap-[8px] items-center [&>svg]:!size-4'>
									<MessageCircleQuestion />
									{t('Ajuda')}
								</div>
								{/* </a> */}
							</ItemMenu>

							<div className='h-[24px] w-px bg-gray-200 flex-shrink-0'></div>

							{SairBtnChildren}
						</div>

					</div>

				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default PerfilMenu;
